/**
 * ExecutiveHeader — sticky glassmorphism top bar
 * Contains: workspace selector, site selector, global IA search, notifications, AI quick, user menu
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import api from '../../lib/api';
import { Search, Bell, Sparkles, ChevronDown, Building2, MapPin, LogOut, CreditCard, User, Menu, X } from 'lucide-react';
import NotificationCenter from '../notifications/NotificationCenter';
import AICreditsIndicator from '../notifications/AICreditsIndicator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

function useDebounced(value, delay = 280) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ employees: [], departments: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debouncedQ = useDebounced(q);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!debouncedQ || debouncedQ.length < 2) {
      setResults({ employees: [], departments: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/employees', { params: { search: debouncedQ, limit: 6 } });
        if (cancelled) return;
        setResults({ employees: data.employees || [], departments: [] });
      } catch (_) {
        if (!cancelled) setResults({ employees: [], departments: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQ]);

  const goto = (path) => { setOpen(false); setQ(''); navigate(path); };

  return (
    <>
      <button onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        data-testid="global-search-trigger"
        className="group hidden md:flex items-center gap-3 w-full max-w-xl rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-sm text-ink-500 hover:border-brand/40 hover:shadow-sm transition-all">
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left truncate">Rechercher un employé, compétence, rapport…</span>
        <kbd className="rounded-md border border-ink-200 bg-ink-50 px-1.5 py-0.5 text-[10px] font-medium text-ink-500">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-24"
          onClick={() => setOpen(false)} data-testid="global-search-modal">
          <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-ink-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-ink-100 px-4 py-3">
              <Search className="h-4 w-4 text-ink-400" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="global-search-input"
                placeholder="Rechercher un employé, compétence, rapport…"
                className="flex-1 bg-transparent text-sm placeholder:text-ink-400 focus:outline-none" />
              <button onClick={() => setOpen(false)} className="rounded-lg p-1 text-ink-400 hover:bg-ink-50">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {!q && (
                <div className="px-3 py-8 text-center text-sm text-ink-500">
                  <Sparkles className="mx-auto mb-2 h-5 w-5 text-ai" />
                  Tapez au moins 2 caractères pour rechercher.
                </div>
              )}
              {q && loading && (
                <div className="px-3 py-6 text-center text-sm text-ink-500">Recherche…</div>
              )}
              {q && !loading && results.employees.length === 0 && q.length >= 2 && (
                <div className="px-3 py-6 text-center text-sm text-ink-500">Aucun résultat.</div>
              )}
              {results.employees.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                    Employés
                  </div>
                  {results.employees.map(e => (
                    <button key={e.id} onClick={() => goto(`/employees/${e.id}`)}
                      data-testid={`search-result-${e.id}`}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-ink-50 transition">
                      <div className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-brand to-brand-hover text-[11px] font-bold text-white">
                        {(e.firstName?.[0] || '') + (e.lastName?.[0] || '')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-ink-900 truncate">{e.firstName} {e.lastName}</div>
                        <div className="text-xs text-ink-500 truncate">{e.jobTitle || '—'} · {e.matricule || ''}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WorkspaceSelector() {
  const { user } = useAuth();
  const { isPro } = usePlan();
  return (
    <div className="flex items-center gap-2">
      <div className="hidden lg:flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-3 py-2">
        <Building2 className="h-4 w-4 text-ink-500" />
        <span className="text-sm font-medium text-ink-900 truncate max-w-[140px]" data-testid="header-company">
          {user?.companyName || 'HR Engine'}
        </span>
        {isPro && (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-amber-700">BUSINESS</span>
        )}
      </div>
      <div className="hidden xl:flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm text-ink-700">
        <MapPin className="h-4 w-4 text-ink-500" />
        <span>Tous les sites</span>
        <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
      </div>
    </div>
  );
}

export default function ExecutiveHeader({ onMobileMenu }) {
  const { user, logout } = useAuth();
  const { isPro } = usePlan();
  const navigate = useNavigate();
  const initials = ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')).toUpperCase() || 'U';

  const logoutAndGo = () => { logout(); navigate('/login'); };

  return (
    <header className="sticky top-0 z-30 border-b border-ink-200/70 bg-white/80 backdrop-blur-xl">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
        <button onClick={onMobileMenu}
          className="lg:hidden -ml-1 rounded-lg p-2 text-ink-700 hover:bg-ink-100">
          <Menu className="h-5 w-5" />
        </button>

        <WorkspaceSelector />

        <div className="ml-auto flex flex-1 items-center justify-end gap-2 md:ml-6 md:justify-between">
          <GlobalSearch />

          <div className="flex items-center gap-1 sm:gap-2 md:ml-3">
            <Link to="/ai-insights" data-testid="header-ai-shortcut"
              className="hidden sm:grid h-9 w-9 place-items-center rounded-xl border border-ink-200 bg-white text-ai hover:border-ai/40 hover:bg-ai-soft transition">
              <Sparkles className="h-4 w-4" />
            </Link>
            <AICreditsIndicator />
            <NotificationCenter />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-user-menu"
                  className="flex items-center gap-2 rounded-xl border border-ink-200 bg-white pl-1 pr-2 py-1 hover:border-brand/40 transition">
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-ink-900 to-ink-700 text-[11px] font-bold text-white">
                    {initials}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-ink-700 truncate max-w-[100px]">
                    {user?.firstName}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div className="px-2 py-2">
                  <p className="text-sm font-semibold text-ink-900">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-ink-500 truncate">{user?.email}</p>
                  <span className="mt-1 inline-block rounded-md bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-600">
                    {user?.role === 'Viewer' ? 'Employé' : user?.role}
                  </span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile"><User className="h-4 w-4 mr-2" /> Mon profil</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/billing/subscription"><CreditCard className="h-4 w-4 mr-2" /> Abonnement</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logoutAndGo} className="text-bad">
                  <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
